import * as z from 'zod';

import { FeeId, FeeLineItemId, PaymentDueId, WorkoutPlanId } from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';
import { AdvanceCategory, AdvanceType, FeeLineItemInput, FeeStatus } from '../events/CreateChargeEvent';
import { InvestorRemittanceSplit } from './InvestorRemittanceSplit';
import { WorkoutPlanType } from './WorkoutPlan';

export const FeeLineItem = FeeLineItemInput.extend({
  id: FeeLineItemId,
});

export type FeeLineItem = z.infer<typeof FeeLineItem>;

/**
 * Escrow Advance
 * Heads up that there is a difference between how lenders speak of escrow advances and how we are handling them in the system.
 *
 * DO NOT add this escrow advance type to the frontend for lenders.
 *
 * For lenders: When a disbursement causes the escrow balance to go negative, that disbursement itself was the escrow advance.
 * The lender needed to pay money out of their own pocket to cover insurance, tax, whatever.
 *
 * In most circumstances, the lender recoups the escrow advance via an escrow analysis (the escrow balance nets out over the specified period of time as the borrower pays the increased escrow amount in their monthly payments)
 *
 * Exception to recouping through escrow advance: payment deferral workout plans.
 * In this case, the lender may optionally bring the negative escrow balance back to $0 and recoup that money at payoff.
 * In order to handle that within our system, we will (automatically):
 *  1) add a deferred escrow advance
 *  2) add that advance amount to the escrow balance
 */
const EscrowAdvanceConfig = z.object({
  advanceType: z.literal('ESCROW'),
});
export type EscrowAdvanceConfig = z.infer<typeof EscrowAdvanceConfig>;

const StandardAdvanceConfig = z.object({
  advanceType: AdvanceType,
  advanceCategory: AdvanceCategory.optional(),
  advanceCategoryDetail: z.string().optional(),
  interestRate: z.number(),
});
export type StandardAdvanceConfig = z.infer<typeof StandardAdvanceConfig>;

export const AdvanceConfig = z.discriminatedUnion('advanceType', [StandardAdvanceConfig, EscrowAdvanceConfig]);
export type AdvanceConfig = z.infer<typeof AdvanceConfig>;

export function isStandardAdvanceConfig(config: AdvanceConfig | undefined): config is StandardAdvanceConfig {
  return AdvanceType.safeParse(config?.advanceType).success;
}

export const ChargeBase = z.object({
  id: FeeId,
  type: z.literal('fee').default('fee'),
  totalAmount: z.number(),
  amountRemaining: z.number(),
  status: FeeStatus,
  chargeDate: zodJSPlainDate,
  comment: z.string().optional(),
  investorRemittanceSplit: InvestorRemittanceSplit.optional(),
  isDeferred: z.boolean(),
  dWorkoutPlan: z
    .object({
      id: WorkoutPlanId,
      type: WorkoutPlanType,
    })
    .optional(),
  createdDate: zodJSInstantDate.optional(),
  updatedAt: zodJSInstantDate.optional(),
  cancelledAt: zodJSInstantDate.optional(), // TODO backfill this for already cancelled charges
});

export const Advance = ChargeBase.extend({
  type: z.literal('advance'),
  advanceConfig: AdvanceConfig.optional(),
  recipient: z.string().optional(),
  advanceInterest: z.number(), // amount remaining
  advancePrincipal: z.number(), // amount remaining

  // amounts charged
  originalPrincipal: z.number(),
  advanceInterestCharged: z.number(),
  advanceInterestUpdatedAt: zodJSInstantDate.optional(),
});

export type Advance = z.infer<typeof Advance>;

export const Fee = ChargeBase.extend({
  type: z.literal('fee'),
  lineItems: z.array(FeeLineItem),
  paymentDueId: PaymentDueId.optional(), // used to track automated fees
});

export type Fee = z.infer<typeof Fee>;

export const Charge = z.discriminatedUnion('type', [Advance, Fee]);
export type Charge = z.infer<typeof Charge>;

export function isFee(item: Charge): item is Fee {
  return item.type === 'fee';
}

export function isAdvance(item: Charge): item is Advance {
  return item.type === 'advance';
}

export function isEscrowAdvance(item: Charge): item is Advance & { advanceConfig: EscrowAdvanceConfig } {
  return isAdvance(item) && item.advanceConfig?.advanceType === 'ESCROW';
}
